import React, { useState, useEffect, useCallback } from 'react';
import { Button, Alert, Spin, Typography, Tag, Space, Divider } from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  LoadingOutlined,
  ScanOutlined,
  ReloadOutlined,
  SafetyOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { biometricAPI } from '../../utils/api';
import { useBiometric } from '../../contexts/BiometricContext';
import { discoverRDPort, rdCaptureLocal } from '../../utils/biometricLocal';

const { Text } = Typography;

/**
 * Builds the PidOptions XML for the Mantra RD Service (MFS500).
 * format="0" = raw template (for 1:N matching)
 * fCount="1" = capture ONE fingerprint
 * posh="UNKNOWN" = required for Mantra MFS500
 */
const buildPidOptions = () =>
  `<?xml version="1.0"?><PidOptions ver="1.0"><Opts fCount="1" fType="0" iCount="0" pCount="0" format="0" pidVer="2.0" timeout="15000" posh="UNKNOWN" env="P"/></PidOptions>`;

/**
 * Extracts errCode from the PidData XML returned by the RD Service.
 */
const parseErrCode = (xmlString) => {
  const match = xmlString.match(/errCode\s*=\s*["']\s*(-?\d+)\s*["']/);
  return match ? match[1] : null;
};

/**
 * FingerprintCapture Component
 * 
 * Uses the backend proxy (/api/biometric/rd-capture) to avoid CORS issues.
 * The backend server-side forwards the request to the local RD Service.
 *
 * Props:
 *  - onCapture(index, template): Called when a fingerprint is captured
 *  - capturedTemplates: [null, null] — 2 templates (null = not captured)
 *  - disabled: boolean
 *  - label: optional label string
 */
const FingerprintCapture = ({
  onCapture,
  capturedTemplates = [null, null],
  disabled = false,
  label = 'Fingerprints',
}) => {
  const { setIsPaused } = useBiometric();
  const [deviceStatus, setDeviceStatus] = useState('checking'); // checking | ready | not_found
  const [connection, setConnection] = useState(null);
  const [capturing, setCapturing] = useState(null); // null | 0 | 1
  const [captureStatus, setCaptureStatus] = useState([null, null]); // 'success' | 'error' | null
  const [errorMsg, setErrorMsg] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [debugData, setDebugData] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);

  const checkDevice = useCallback(async () => {
    setDeviceStatus('checking');
    setErrorMsg('');
    setDebugData(null);
    try {
      // 1. Try local hardware first
      const localConn = await discoverRDPort();
      if (localConn) {
        setConnection(localConn);
        setDeviceStatus('ready');
        return;
      }

      // 2. Fallback to server proxy
      const response = await biometricAPI.rdStatus();
      if (response.data.status === 'ready') {
        setDeviceStatus('ready');
      } else {
        setDeviceStatus('not_found');
      }
    } catch {
      setDeviceStatus('not_found');
    }
  }, []);

  const handleTroubleshoot = async () => {
    setDebugLoading(true);
    try {
      const response = await biometricAPI.rdDebug();
      setDebugData(response.data);
    } catch (err) {
      setErrorMsg('Failed to run diagnostics.');
    } finally {
      setDebugLoading(false);
    }
  };

  useEffect(() => {
    checkDevice();
  }, [checkDevice]);

  const handleCapture = async (index) => {
    let retries = 0;
    const maxRetries = 5;

    const performCapture = async () => {
      try {
        setIsPaused(true); // 🚀 Pause global scan loop
        setCapturing(index);
        setErrorMsg('');
        const newStatus = [...captureStatus];
        newStatus[index] = null;
        setCaptureStatus(newStatus);

        // Small delay to let any in-flight background request hit the backend
        if (retries === 0) await new Promise(r => setTimeout(r, 500));

        const pidOptions = buildPidOptions(15000);
        
        let xmlText;
        if (connection) {
          // 🚀 REMOTE FIX: Capture locally from browser
          xmlText = await rdCaptureLocal(connection, pidOptions);
        } else {
          // Legacy: Capture via server proxy
          const response = await biometricAPI.rdCapture(pidOptions);
          xmlText = response.data;
        }

        const errCode = parseErrCode(String(xmlText));

        if (errCode === '0') {
          const updated = [...newStatus];
          updated[index] = 'success';
          setCaptureStatus(updated);
          onCapture(index, xmlText);
          return true; // Success
        } else if (errCode === '-2023' && retries < maxRetries) {
          // Device busy - wait and retry silently
          retries++;
          await new Promise(r => setTimeout(r, 2000));
          return await performCapture();
        } else {
          const code = errCode || 'unknown';
          throw new Error(`Device returned errCode=${code}. Please place your finger firmly and try again.`);
        }
      } catch (err) {
        const msg = err.response?.data?.message || err.message || 'Capture failed.';
        setErrorMsg(msg);
        const updated = [...captureStatus];
        updated[index] = 'error';
        setCaptureStatus(updated);
        onCapture(index, null);
        return false;
      }
    };

    await performCapture();
    setCapturing(null);
    setIsPaused(false); // 🚀 Resume global scan loop
  };

  const getStatusIcon = (index) => {
    if (capturing === index) return <LoadingOutlined spin style={{ fontSize: 28, color: '#1890ff' }} />;
    if (captureStatus[index] === 'success' || capturedTemplates[index])
      return <CheckCircleFilled style={{ fontSize: 28, color: '#52c41a' }} />;
    if (captureStatus[index] === 'error')
      return <CloseCircleFilled style={{ fontSize: 28, color: '#ff4d4f' }} />;
    return <ScanOutlined style={{ fontSize: 28, color: '#d9d9d9' }} />;
  };

  const fingerLabels = ['Right Thumb', 'Left Thumb'];

  return (
    <div style={{ marginBottom: 8 }}>
      {/* Device Status Banner */}
      {deviceStatus === 'checking' && (
        <Alert
          type="info"
          icon={<Spin size="small" />}
          message="Checking local fingerprint device..."
          style={{ marginBottom: 12 }}
          showIcon
        />
      )}

      {deviceStatus === 'not_found' && (
        <Alert
          type="warning"
          message="Fingerprint device not detected"
          description={
            <div>
              <p>The browser could not reach the Mantra RD Service on your computer (127.0.0.1). Please ensure:</p>
              <ul style={{ marginBottom: 8, paddingLeft: 20 }}>
                <li>The <strong>Mantra RD Service</strong> is installed and running (<code>services.msc</code>)</li>
                <li>The <strong>Mantra MFS500</strong> scanner is plugged in via USB</li>
                <li>You are using a supported browser (Chrome/Edge recommended)</li>
              </ul>
              <Space>
                <Button size="small" icon={<ReloadOutlined />} onClick={checkDevice}>
                  Retry Detection
                </Button>
                <Button 
                  size="small" 
                  onClick={handleTroubleshoot} 
                  loading={debugLoading}
                >
                  Troubleshoot
                </Button>
              </Space>

              {debugData && (
                <div style={{ marginTop: 12, maxHeight: 150, overflowY: 'auto', background: '#000', color: '#0f0', padding: 8, borderRadius: 4, fontFamily: 'monospace', fontSize: '11px' }}>
                  <div style={{ fontWeight: 'bold', borderBottom: '1px solid #0f0', marginBottom: 4 }}>
                    Diagnostic Sweep: {debugData.summary}
                  </div>
                  {debugData.probes.map((p, i) => (
                    <div key={i} style={{ opacity: p.status === 'found' ? 1 : 0.6 }}>
                      {p.protocol}://127.0.0.1:{p.port} - {p.status.toUpperCase()} {p.detail && `(${p.detail})`}
                    </div>
                  ))}
                </div>
              )}
            </div>
          }
          style={{ marginBottom: 12 }}
          showIcon
        />
      )}

      {deviceStatus === 'ready' && (
        <Alert
          type="success"
          message="Fingerprint device ready"
          style={{ marginBottom: 12 }}
          showIcon
        />
      )}

      {errorMsg && (
        <Alert
          type="error"
          message={errorMsg}
          style={{ marginBottom: 12 }}
          showIcon
          closable
          onClose={() => setErrorMsg('')}
        />
      )}

      {/* Finger Capture Buttons */}
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: '24px' }}>
        {[0, 1].map((index) => {
          const isCaptured = !!(captureStatus[index] === 'success' || capturedTemplates[index]);
          const isError = captureStatus[index] === 'error';
          const isCapturing = capturing === index;

          return (
            <div
              key={index}
              style={{
                flex: 1,
                minWidth: 180,
                border: `2px dashed ${isCaptured ? '#52c41a' : isError ? '#ff4d4f' : '#d9d9d9'}`,
                borderRadius: 12,
                padding: '20px 16px',
                textAlign: 'center',
                background: isCaptured ? '#f6ffed' : isError ? '#fff2f0' : '#fafafa',
                transition: 'all 0.3s ease',
              }}
            >
              <div style={{ marginBottom: 8 }}>{getStatusIcon(index)}</div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                {fingerLabels[index]}
              </Text>
              {isCaptured && (
                <Tag color="success" style={{ marginBottom: 8 }}>Captured ✓</Tag>
              )}
              {isError && (
                <Tag color="error" style={{ marginBottom: 8 }}>Status Failed</Tag>
              )}
              <Button
                type={isCaptured ? 'default' : 'primary'}
                icon={isCaptured ? <ReloadOutlined /> : <SafetyOutlined />}
                loading={isCapturing}
                onClick={() => handleCapture(index)}
                block
                disabled={
                  disabled ||
                  deviceStatus !== 'ready' ||
                  (capturing !== null && capturing !== index)
                }
              >
                {isCapturing ? 'Scanning...' : isCaptured ? `Scan ${fingerLabels[index]} Again` : `Scan ${fingerLabels[index]}`}
              </Button>
            </div>
          );
        })}
      </div>

      {/* 🚀 New: Troubleshooting Footer */}
      <div style={{ 
        marginTop: '16px', 
        paddingTop: '16px', 
        borderTop: '1px solid #eee', 
        textAlign: 'center' 
      }}>
        <Space split={<Divider type="vertical" />}>
          <Button 
            size="small" 
            icon={<ReloadOutlined />} 
            onClick={() => {
              setCaptureStatus([null, null]);
              setErrorMsg('');
              checkDevice();
            }}
          >
            Re-Check Device
          </Button>
          <Button 
            size="small" 
            type="link" 
            onClick={() => setShowHelp(!showHelp)}
          >
            {showHelp ? "Hide Troubleshooting" : "Stuck? (Error 720 Help)"}
          </Button>
        </Space>
        
        {showHelp && (
          <div style={{ 
            marginTop: '12px', 
            padding: '12px', 
            background: '#fff', 
            border: '1px solid #d9d9d9', 
            borderRadius: '8px', 
            textAlign: 'left',
            fontSize: '12px',
            color: '#555'
          }}>
            <p><strong>If you keep getting "Device Not Ready" (Error 720):</strong></p>
            <ul style={{ paddingLeft: '20px', marginBottom: '8px' }}>
              <li><strong>Physical Reset</strong>: Unplug the MFS500, wait 5 seconds, then plug it back in.</li>
              <li><strong>Service Reset</strong>: Open <code>services.msc</code>, find <strong>"Mantra RD Service"</strong>, right-click and select <strong>"Restart"</strong>.</li>
              <li><strong>Wait</strong>: After plugging in, wait for the scanner's LED to flash (about 10 seconds) before scanning.</li>
            </ul>
            <p style={{ margin: 0 }}>The scanner should ideally "BEEP" or flash its LED when it is ready for your finger.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FingerprintCapture;
